import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectors } from 'darwin_core';

import { buildWeeklySchedule, getScheduleNowNext } from './formatSchedule.js';

// A read-only view of a zone's REPORTED weekly schedule. zoneId is the
// darwin_core tuple [prtId, endpointId]; the same [prtId, endpointId] the tiles
// use. This renders reported state only — there are no editing controls and it
// dispatches nothing. It deliberately does NOT touch the draft/pending schedule
// slices (the mobile editor's model) nor state.schedule.selectedScheduleDay: the
// whole week is rendered from the reported schedule so no editor-only UI state
// needs to be set.
//
// The raw nested schedule is read as a stable store reference (via find, not a
// re-mapping selector) so useSelector does not churn; the display model and the
// current/next highlight are then derived from it with useMemo. The comfort/
// reduced setpoints are read as primitives so getHpSetpoint's fresh return
// object never trips useSelector's stability check.
export default function ZoneSchedule({ homeId, zoneId, id }) {
  const rawSchedule = useSelector((state) => {
    const home = state.homeList.homes.find((h) => h.id === homeId);
    const zone = home?.zones?.find(
      (z) => z.zoneId?.join('_') === zoneId.join('_'),
    );
    return zone?.schedule;
  });
  const online = useSelector((state) =>
    selectors.isZoneOnline(state, homeId, zoneId),
  );
  // HPI heat-pump events say "comfort"/"reduced"; the actual temperatures live on
  // the zone. getHpSetpoint returns a fresh object each call, so pull the two
  // centidegree numbers out as primitives to keep the subscription stable.
  const comfortSetpoint = useSelector(
    (state) => selectors.getHpSetpoint(state, homeId, zoneId).comfortSetpoint,
  );
  const reducedSetpoint = useSelector(
    (state) => selectors.getHpSetpoint(state, homeId, zoneId).reducedSetpoint,
  );

  const week = useMemo(
    () =>
      buildWeeklySchedule(rawSchedule, { comfortSetpoint, reducedSetpoint }),
    [rawSchedule, comfortSetpoint, reducedSetpoint],
  );

  // Current/next event, matched back to the rendered rows by day + start time.
  // Derived from the same reported schedule the rows are built from (not the
  // state-level selectors, which layer in a live boost/DSR override that would
  // drop the Next badge); getScheduleNowNext sanitises before weekifying so a
  // partial frame renders rows without the current/next path throwing.
  const { currentDay, currentStart, nextDay, nextStart } = useMemo(
    () => getScheduleNowNext(rawSchedule),
    [rawSchedule],
  );

  if (week.length === 0) {
    return (
      <div className="zone-schedule" id={id}>
        <p className="zone-schedule__empty" role="status">
          No schedule has been reported for this zone.
        </p>
      </div>
    );
  }

  return (
    <div className="zone-schedule" id={id}>
      {!online && (
        <p className="zone-schedule__offline">
          This zone is offline — the schedule below is the last reported.
        </p>
      )}
      <ol className="zone-schedule__days">
        {week.map(({ day, events }) => (
          <li key={day} className="zone-schedule__day">
            <h2 className="zone-schedule__day-name">{day}</h2>
            {events.length === 0 ? (
              <p className="zone-schedule__none">No events</p>
            ) : (
              <ul className="zone-schedule__events">
                {events.map((event) => {
                  const isCurrent =
                    day === currentDay && event.startTime === currentStart;
                  const isNext =
                    !isCurrent &&
                    day === nextDay &&
                    event.startTime === nextStart;
                  const className = `zone-schedule__event${
                    isCurrent ? ' zone-schedule__event--current' : ''
                  }${isNext ? ' zone-schedule__event--next' : ''}`;
                  return (
                    <li
                      key={`${day}_${event.startTime}`}
                      className={className}
                      aria-current={isCurrent ? 'time' : undefined}
                    >
                      <span className="zone-schedule__time">
                        {event.timeLabel}
                      </span>
                      <span className="zone-schedule__target">
                        {event.label}
                      </span>
                      {isCurrent && (
                        <span className="zone-schedule__badge">Now</span>
                      )}
                      {isNext && (
                        <span className="zone-schedule__badge zone-schedule__badge--next">
                          Next
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}
